// Admin reservations list — search + filter chips, quick stats, and links to detail.
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Plus, Filter } from "lucide-react";
import { toast } from "sonner";
import { adminApi, formatApiError } from "@/lib/adminAuth";

const STATUS_TABS = [
    { key: "all",         label: "All" },
    { key: "pending",     label: "Pending" },
    { key: "confirmed",   label: "Confirmed" },
    { key: "checked_in",  label: "In-house" },
    { key: "checked_out", label: "Departed" },
    { key: "cancelled",   label: "Cancelled" },
    { key: "no_show",     label: "No-show" },
];

const STATUS_STYLE = {
    pending:      "bg-amber-100 text-amber-800",
    confirmed:    "bg-jade/15 text-jade",
    checked_in:   "bg-emerald-100 text-emerald-700",
    checked_out:  "bg-gray-100 text-gray-600",
    cancelled:    "bg-red-100 text-red-600",
    no_show:      "bg-red-50 text-red-500",
};
const PAY_STYLE = {
    unpaid:              "bg-gray-100 text-gray-600",
    deposit_paid:        "bg-blue-50 text-blue-700",
    partially_paid:      "bg-amber-50 text-amber-700",
    paid_in_full:        "bg-emerald-50 text-emerald-700",
    partially_refunded:  "bg-orange-50 text-orange-700",
    refunded:            "bg-red-50 text-red-600",
    failed:              "bg-red-100 text-red-700",
};

function StatusChip({ status, kind = "status" }) {
    const styles = kind === "status" ? STATUS_STYLE : PAY_STYLE;
    return (
        <span className={`inline-block text-[10px] uppercase tracking-[0.14em] px-2 py-1 rounded-full ${styles[status] || "bg-gray-100 text-gray-600"}`}>
            {String(status || "-").replace(/_/g, " ")}
        </span>
    );
}

function usd(v) { return `$${Number(v || 0).toFixed(2)}`; }
function fmtDate(s) {
    if (!s) return "—";
    try {
        const d = new Date(s + (s.length === 10 ? "T00:00:00" : ""));
        return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    } catch { return s; }
}

export default function AdminReservations() {
    const [rows, setRows] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [paymentFilter, setPaymentFilter] = useState("all");

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (q.trim()) params.set("q", q.trim());
            if (statusFilter !== "all") params.set("status", statusFilter);
            if (paymentFilter !== "all") params.set("payment_status", paymentFilter);
            const [{ data }, { data: st }] = await Promise.all([
                adminApi.get(`/admin/reservations?${params.toString()}`),
                adminApi.get("/admin/reservations/stats"),
            ]);
            setRows(data);
            setStats(st);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    // debounce search
    useEffect(() => {
        const t = setTimeout(load, 250);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [q, statusFilter, paymentFilter]);

    const countPerStatus = useMemo(() => {
        const m = { all: rows.length };
        for (const r of rows) m[r.status] = (m[r.status] || 0) + 1;
        return m;
    }, [rows]);

    return (
        <div>
            <header className="mb-6 flex flex-wrap gap-4 items-end justify-between">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.28em] text-gold">Reservations</div>
                    <h1 className="font-display text-4xl md:text-5xl text-[#0b1f22] mt-3">Bookings</h1>
                    <p className="text-[#0b1f22]/60 mt-2 text-sm">Every reservation, from every source. Click a row to edit, record payments, cancel, or refund.</p>
                </div>
                <Link to="/admin/reservations/new" data-testid="new-reservation" className="bg-gold text-navy px-5 py-3 rounded-full text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-gold/90 inline-flex items-center gap-2">
                    <Plus size={13} /> New reservation
                </Link>
            </header>

            {/* Live stats */}
            {stats && (
                <section className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3 mb-6">
                    {[
                        { k: "Today's arrivals", v: stats.arrivals_today, t: "arrivals" },
                        { k: "Today's departures", v: stats.departures_today, t: "departures" },
                        { k: "In-house now", v: stats.in_house, t: "in_house" },
                        { k: "Pending", v: stats.pending, t: "pending" },
                        { k: "Revenue · month", v: usd(stats.revenue_paid_this_month), t: "revenue_month" },
                    ].map((k) => (
                        <div key={k.k} data-testid={`stat-${k.t}`} className="bg-white border border-[#0b1f22]/10 rounded-xl px-4 py-3">
                            <div className="text-[10px] uppercase tracking-[0.2em] text-[#0b1f22]/50">{k.k}</div>
                            <div className="font-display text-2xl text-[#0b1f22] mt-1">{k.v}</div>
                        </div>
                    ))}
                </section>
            )}

            {/* Search + tabs */}
            <section className="bg-white border border-[#0b1f22]/10 rounded-2xl p-4 mb-4">
                <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2 border border-[#0b1f22]/15 rounded-full px-4 py-2 flex-1 min-w-[260px]">
                        <Search size={14} className="text-[#0b1f22]/40" />
                        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, phone, confirmation…"
                            data-testid="reservations-search"
                            className="flex-1 bg-transparent outline-none text-sm" />
                    </div>
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-[#0b1f22]/50"><Filter size={12} /> Payment:</div>
                    <select value={paymentFilter} onChange={(e) => setPaymentFilter(e.target.value)}
                        data-testid="payment-filter"
                        className="border border-[#0b1f22]/15 rounded-full px-3 py-2 text-[11px] uppercase tracking-[0.14em] bg-white">
                        <option value="all">All</option>
                        <option value="unpaid">Unpaid</option>
                        <option value="deposit_paid">Deposit paid</option>
                        <option value="partially_paid">Partially paid</option>
                        <option value="paid_in_full">Paid in full</option>
                        <option value="partially_refunded">Partially refunded</option>
                        <option value="refunded">Refunded</option>
                    </select>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                    {STATUS_TABS.map((tab) => (
                        <button key={tab.key} onClick={() => setStatusFilter(tab.key)}
                            data-testid={`tab-${tab.key}`}
                            className={`text-[11px] uppercase tracking-[0.14em] px-3 py-1.5 rounded-full transition-colors ${statusFilter === tab.key ? "bg-[#0b1f22] text-ivory" : "bg-[#0b1f22]/[0.06] text-[#0b1f22]/70 hover:bg-[#0b1f22]/10"}`}>
                            {tab.label} <span className="opacity-60 ml-1">{countPerStatus[tab.key] || 0}</span>
                        </button>
                    ))}
                </div>
            </section>

            {/* Table */}
            <div className="bg-white border border-[#0b1f22]/10 rounded-2xl overflow-hidden">
                {loading && rows.length === 0 ? (
                    <div className="p-10 text-sm text-[#0b1f22]/50 text-center">Loading reservations…</div>
                ) : rows.length === 0 ? (
                    <div className="p-10 text-sm text-[#0b1f22]/50 text-center">No reservations match those filters.</div>
                ) : (
                    <table className="w-full text-sm" data-testid="reservations-table">
                        <thead className="bg-[#0b1f22]/[0.04] text-[10px] uppercase tracking-[0.16em] text-[#0b1f22]/60">
                            <tr>
                                <th className="text-left px-5 py-3">Confirmation</th>
                                <th className="text-left px-5 py-3">Guest</th>
                                <th className="text-left px-5 py-3">Dates</th>
                                <th className="text-left px-5 py-3">Room</th>
                                <th className="text-left px-5 py-3">Status</th>
                                <th className="text-left px-5 py-3">Payment</th>
                                <th className="text-right px-5 py-3">Total</th>
                                <th className="text-right px-5 py-3">Balance</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.id} className="border-t border-[#0b1f22]/5 hover:bg-[#0b1f22]/[0.02]">
                                    <td className="px-5 py-4">
                                        <Link to={`/admin/reservations/${r.id}`} data-testid={`row-${r.confirmation_number}`} className="font-mono text-[13px] text-gold hover:underline">{r.confirmation_number}</Link>
                                        <div className="text-[10px] text-[#0b1f22]/45 uppercase tracking-[0.15em] mt-0.5">{r.source}</div>
                                    </td>
                                    <td className="px-5 py-4">
                                        <div className="font-medium">{r.guest_name}</div>
                                        <div className="text-[11px] text-[#0b1f22]/60">{r.email}</div>
                                    </td>
                                    <td className="px-5 py-4">
                                        <div>{fmtDate(r.check_in)}</div>
                                        <div className="text-[11px] text-[#0b1f22]/55">{r.nights} nights → {fmtDate(r.check_out)}</div>
                                    </td>
                                    <td className="px-5 py-4">
                                        <div className="text-[13px]">{r.room_name || r.room_slug || "—"}</div>
                                        <div className="text-[11px] text-[#0b1f22]/50">{r.adults} adult{r.adults === 1 ? "" : "s"}{r.children ? ` + ${r.children}` : ""}</div>
                                    </td>
                                    <td className="px-5 py-4"><StatusChip status={r.status} kind="status" /></td>
                                    <td className="px-5 py-4"><StatusChip status={r.payment_status} kind="payment" /></td>
                                    <td className="px-5 py-4 text-right font-medium">{usd(r.total)}</td>
                                    <td className="px-5 py-4 text-right"><span className={Number(r.remaining_balance) > 0 ? "text-amber-700 font-medium" : "text-[#0b1f22]/50"}>{usd(r.remaining_balance)}</span></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
